import { AlertCircle, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';

interface QueryErrorStateProps {
  title?: string;
  description?: string;
  error?: Error | null;
  onRetry?: () => void;
}

/**
 * Error state component for React Query failures
 * Shows user-friendly error message with retry button
 */
export function QueryErrorState({ title, description, error, onRetry }: QueryErrorStateProps) {
  const { t } = useTranslation('common');
  const displayTitle = title || t('errors.loadFailed');
  const errorMessage = description || error?.message || t('errors.genericRetry');

  return (
    <div className="flex flex-col items-center justify-center p-8 text-center">
      <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mb-3">
        <AlertCircle className="w-6 h-6 text-destructive" />
      </div>
      <h3 className="text-base font-semibold mb-1">{displayTitle}</h3>
      <p className="text-sm text-muted-foreground mb-4 max-w-sm">{errorMessage}</p>
      {onRetry && (
        <Button onClick={onRetry} variant="outline" size="sm" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          {t('actions.retry')}
        </Button>
      )}
    </div>
  );
}
